// Cell rendering utilities for AG Grid

import { DateColumn, ShiftMetadata } from './types';

/**
 * Helper: Validate and extract shift metadata from cell value
 */
function extractShiftMetadata(value: unknown): ShiftMetadata | null {
    if (!value || value === '') {
        return null;
    }
    
    if (typeof value !== 'object' || value === null) {
        return null;
    }
    
    return value as ShiftMetadata;
}

/**
 * Helper: Get template color from context
 */
function getTemplateColor(
    metadata: ShiftMetadata,
    context: Record<string, unknown>
): string | undefined {
    if (metadata.shiftTemplateColor) {
        return metadata.shiftTemplateColor;
    }
    
    const shiftTemplates = (context as { shiftTemplates?: Array<{ id: string; color?: string; colour?: string }> }).shiftTemplates;
    const template = shiftTemplates?.find((tpl) => tpl.id === metadata.shiftTemplateId);
    
    return template ? (template.color || template.colour || '#cccccc') : undefined;
}

/**
 * Helper: Get location color from context
 */
function getLocationColor(
    metadata: ShiftMetadata,
    context: Record<string, unknown>
): string | undefined {
    const locations = (context as { locations?: Array<{ id: string; color?: string; colour?: string }> }).locations;
    const location = locations?.find((loc) => loc.id === metadata.location?.id);
    
    return location ? (location.color || location.colour || '#cccccc') : undefined;
}

/**
 * Helper: Determine dot color based on mode and metadata
 */
function getDotColor(
    metadata: ShiftMetadata,
    mode: string,
    context: Record<string, unknown>
): string | undefined {
    // Leave shifts - pink dot (matches Angular agentLeaveColour)
    if (metadata.isLeave) {
        return '#f8c7c7';
    }
    
    if (mode === 'TEMPLATES' && metadata.shiftTemplateId) {
        return getTemplateColor(metadata, context);
    }
    
    if (mode === 'LOCATIONS' && metadata.location?.id) {
        return getLocationColor(metadata, context);
    }
    
    return undefined;
}

/**
 * Helper: Normalize hex color (ensure # prefix)
 */
function normalizeHexColor(color: string | undefined): string | undefined {
    if (!color) return undefined;
    
    if (color.startsWith('#')) {
        return color;
    }
    
    if (color.match(/^[0-9a-fA-F]{6}$/)) {
        return '#' + color;
    }
    
    return color;
}

/**
 * Helper: Escape HTML entities for title attribute
 */
function escapeHtmlForTitle(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Helper: Render cell HTML with optional colored dot and tooltip
 *
 * @param displayText - The text to display in the cell (e.g., "09:00 - 17:00")
 * @param dotColor - Optional dot color for shift templates/locations
 * @param shiftComment - Optional shift comment for tooltip
 * @param partialLeaveDuration - Optional partial leave duration info for tooltip
 * @returns HTML string for the cell content
 */
function renderCellHtml(
    displayText: string,
    dotColor?: string,
    shiftComment?: string,
    partialLeaveDuration?: string
): string {
    const clockIcon = partialLeaveDuration
        ? `<span 
            class="shift-partial-leave-icon" 
            role="button"
            tabindex="0"
            aria-label="Partial leave information: ${escapeHtmlForTitle(partialLeaveDuration)}"
            title="${escapeHtmlForTitle(partialLeaveDuration)}"
            style="
                display: inline-flex;
                align-items: center;
                justify-content: center;
                margin-left: 4px;
                cursor: pointer;
                color: #000000;
                font-size: 14px;
                font-weight: 600;
                line-height: 1.2;
                vertical-align: middle;
                flex-shrink: 0;
                margin-top: -2px;
            "
        >        <svg width="14" height="14" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M11.0312 5C10.7604 5 10.5208 5.10417 10.3125 5.3125C10.125 5.5 10.0312 5.72917 10.0312 6V12C10.0312 12.2083 10.0833 12.3958 10.1875 12.5625C10.3125 12.7292 10.4688 12.8542 10.6562 12.9375L15.6562 14.9375C15.9062 15.0417 16.1562 15.0417 16.4062 14.9375C16.6771 14.8125 16.8646 14.625 16.9688 14.375C17.0729 14.125 17.0625 13.875 16.9375 13.625C16.8333 13.3542 16.6562 13.1667 16.4062 13.0625L12.0312 11.3125V6C12.0312 5.72917 11.9271 5.5 11.7188 5.3125C11.5312 5.10417 11.3021 5 11.0312 5ZM11.0312 1C11.0312 0.729167 11.125 0.5 11.3125 0.3125C11.5208 0.104167 11.7604 0 12.0312 0C13.5521 0 14.9792 0.291667 16.3125 0.875C17.6458 1.4375 18.8125 2.21875 19.8125 3.21875C20.8125 4.21875 21.5938 5.38542 22.1562 6.71875C22.7396 8.05208 23.0312 9.47917 23.0312 11C23.0312 12.5208 22.7396 13.9479 22.1562 15.2812C21.5938 16.6146 20.8125 17.7812 19.8125 18.7812C18.8125 19.7812 17.6458 20.5625 16.3125 21.125C14.9792 21.7083 13.5521 22 12.0312 22C10.5104 22 9.08333 21.7083 7.75 21.125C6.41667 20.5625 5.25 19.7812 4.25 18.7812C3.25 17.7812 2.46875 16.6146 1.90625 15.2812C1.32292 13.9479 1.03125 12.5208 1.03125 11C1.03125 9.8125 1.20833 8.67708 1.5625 7.59375C1.91667 6.51042 2.41667 5.52083 3.0625 4.625L0.96875 4.5C0.697917 4.47917 0.46875 4.36458 0.28125 4.15625C0.09375 3.94792 0.0104167 3.70833 0.03125 3.4375C0.0520833 3.16667 0.166667 2.9375 0.375 2.75C0.583333 2.5625 0.822917 2.47917 1.09375 2.5L5.21875 2.75C5.42708 2.75 5.61458 2.82292 5.78125 2.96875C5.96875 3.11458 6.08333 3.29167 6.125 3.5L7 7.28125C7.0625 7.55208 7.02083 7.80208 6.875 8.03125C6.72917 8.26042 6.52083 8.40625 6.25 8.46875C5.97917 8.53125 5.72917 8.48958 5.5 8.34375C5.27083 8.19792 5.125 7.98958 5.0625 7.71875L4.625 5.875C4.125 6.60417 3.72917 7.40625 3.4375 8.28125C3.16667 9.13542 3.03125 10.0417 3.03125 11C3.03125 12.25 3.27083 13.4167 3.75 14.5C4.20833 15.6042 4.84375 16.5625 5.65625 17.375C6.46875 18.1875 7.42708 18.8229 8.53125 19.2812C9.61458 19.7604 10.7812 20 12.0312 20C13.2812 20 14.4479 19.7604 15.5312 19.2812C16.6354 18.8229 17.5938 18.1875 18.4062 17.375C19.2188 16.5625 19.8542 15.6042 20.3125 14.5C20.7917 13.4167 21.0312 12.25 21.0312 11C21.0312 9.75 20.7917 8.58333 20.3125 7.5C19.8542 6.39583 19.2188 5.4375 18.4062 4.625C17.5938 3.8125 16.6354 3.17708 15.5312 2.71875C14.4479 2.23958 13.2812 2 12.0312 2C11.7604 2 11.5208 1.90625 11.3125 1.71875C11.125 1.51042 11.0312 1.27083 11.0312 1Z" fill="#000000"/></svg>
</span>`
        : '';
    
    const tooltipIcon = shiftComment
        ? `<span 
            class="shift-tooltip-icon" 
            role="button"
            tabindex="0"
            aria-label="Shift comment: ${escapeHtmlForTitle(shiftComment)}"
            title="${escapeHtmlForTitle(shiftComment)}"
            style="
                display: inline-flex;
                align-items: center;
                justify-content: center;
                margin-left: 4px;
                cursor: pointer;
                color: #000000;
                font-size: 14px;
                font-weight: 600;
                line-height: 1.2;
                vertical-align: top;
                flex-shrink: 0;
            "
        >ⓘ</span>`
        : '';

    // Only render dot span when there's a color to show
    const dotHtml = dotColor
        ? `<span style="
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background-color: ${dotColor};
            display: inline-block;
            flex-shrink: 0;
            border: 1px solid rgba(0,0,0,0.1);
        "></span>`
        : '';

    return `
        <div style="display: flex; align-items: center; gap: 6px; min-height: 20px;">
            ${dotHtml}
            <span style="white-space: pre-line;">${displayText}</span>${tooltipIcon}${clockIcon}
        </div>
    `;
}

/**
 * Helper: Get color mode from context
 */
function getColorMode(context: Record<string, unknown>): string {
    return (context as { colorMode?: string }).colorMode || 'TEMPLATES';
}

/**
 * Helper: Render multiple shifts with individual colored dots
 */
function renderMultipleShifts(
    shifts: Array<{
        displayText: string;
        isLeave: boolean;
        isPartialLeave?: boolean;
        partialLeaveDuration?: string;
        shiftTemplateId?: string;
        shiftTemplateName?: string;
        shiftTemplateColor?: string;
        shiftComment?: string;
        location?: { id: string; name: string; color?: string };
    }>,
    mode: string,
    context: Record<string, unknown>
): string {
    const shiftHtmlLines = shifts.map((shift) => {
        const shiftMetadata: ShiftMetadata = {
            displayText: shift.displayText,
            isHidden: false,
            isLeave: shift.isLeave,
            isPartialLeave: shift.isPartialLeave,
            partialLeaveDuration: shift.partialLeaveDuration,
            shiftTemplateId: shift.shiftTemplateId,
            shiftTemplateName: shift.shiftTemplateName,
            shiftTemplateColor: shift.shiftTemplateColor,
            shiftComment: shift.shiftComment,
            location: shift.location
        };
        
        // Get color for this specific shift
        const rawDotColor = getDotColor(shiftMetadata, mode, context);
        const dotColor = normalizeHexColor(rawDotColor);
        
        const clockIcon = shift.partialLeaveDuration
            ? `<span 
                class="shift-partial-leave-icon" 
                role="button"
                tabindex="0"
                aria-label="Partial leave information: ${escapeHtmlForTitle(shift.partialLeaveDuration)}"
                title="${escapeHtmlForTitle(shift.partialLeaveDuration)}"
                style="
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    margin-left: 4px;
                    cursor: pointer;
                    color: #000000;
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 1.2;
                    vertical-align: middle;
                    flex-shrink: 0;
                    margin-top: -2px;
                "
            >        <svg width="14" height="14" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M11.0312 5C10.7604 5 10.5208 5.10417 10.3125 5.3125C10.125 5.5 10.0312 5.72917 10.0312 6V12C10.0312 12.2083 10.0833 12.3958 10.1875 12.5625C10.3125 12.7292 10.4688 12.8542 10.6562 12.9375L15.6562 14.9375C15.9062 15.0417 16.1562 15.0417 16.4062 14.9375C16.6771 14.8125 16.8646 14.625 16.9688 14.375C17.0729 14.125 17.0625 13.875 16.9375 13.625C16.8333 13.3542 16.6562 13.1667 16.4062 13.0625L12.0312 11.3125V6C12.0312 5.72917 11.9271 5.5 11.7188 5.3125C11.5312 5.10417 11.3021 5 11.0312 5ZM11.0312 1C11.0312 0.729167 11.125 0.5 11.3125 0.3125C11.5208 0.104167 11.7604 0 12.0312 0C13.5521 0 14.9792 0.291667 16.3125 0.875C17.6458 1.4375 18.8125 2.21875 19.8125 3.21875C20.8125 4.21875 21.5938 5.38542 22.1562 6.71875C22.7396 8.05208 23.0312 9.47917 23.0312 11C23.0312 12.5208 22.7396 13.9479 22.1562 15.2812C21.5938 16.6146 20.8125 17.7812 19.8125 18.7812C18.8125 19.7812 17.6458 20.5625 16.3125 21.125C14.9792 21.7083 13.5521 22 12.0312 22C10.5104 22 9.08333 21.7083 7.75 21.125C6.41667 20.5625 5.25 19.7812 4.25 18.7812C3.25 17.7812 2.46875 16.6146 1.90625 15.2812C1.32292 13.9479 1.03125 12.5208 1.03125 11C1.03125 9.8125 1.20833 8.67708 1.5625 7.59375C1.91667 6.51042 2.41667 5.52083 3.0625 4.625L0.96875 4.5C0.697917 4.47917 0.46875 4.36458 0.28125 4.15625C0.09375 3.94792 0.0104167 3.70833 0.03125 3.4375C0.0520833 3.16667 0.166667 2.9375 0.375 2.75C0.583333 2.5625 0.822917 2.47917 1.09375 2.5L5.21875 2.75C5.42708 2.75 5.61458 2.82292 5.78125 2.96875C5.96875 3.11458 6.08333 3.29167 6.125 3.5L7 7.28125C7.0625 7.55208 7.02083 7.80208 6.875 8.03125C6.72917 8.26042 6.52083 8.40625 6.25 8.46875C5.97917 8.53125 5.72917 8.48958 5.5 8.34375C5.27083 8.19792 5.125 7.98958 5.0625 7.71875L4.625 5.875C4.125 6.60417 3.72917 7.40625 3.4375 8.28125C3.16667 9.13542 3.03125 10.0417 3.03125 11C3.03125 12.25 3.27083 13.4167 3.75 14.5C4.20833 15.6042 4.84375 16.5625 5.65625 17.375C6.46875 18.1875 7.42708 18.8229 8.53125 19.2812C9.61458 19.7604 10.7812 20 12.0312 20C13.2812 20 14.4479 19.7604 15.5312 19.2812C16.6354 18.8229 17.5938 18.1875 18.4062 17.375C19.2188 16.5625 19.8542 15.6042 20.3125 14.5C20.7917 13.4167 21.0312 12.25 21.0312 11C21.0312 9.75 20.7917 8.58333 20.3125 7.5C19.8542 6.39583 19.2188 5.4375 18.4062 4.625C17.5938 3.8125 16.6354 3.17708 15.5312 2.71875C14.4479 2.23958 13.2812 2 12.0312 2C11.7604 2 11.5208 1.90625 11.3125 1.71875C11.125 1.51042 11.0312 1.27083 11.0312 1Z" fill="#000000"/></svg>

</span>`
            : '';
        
        const tooltipIcon = shift.shiftComment
            ? `<span 
                class="shift-tooltip-icon" 
                role="button"
                tabindex="0"
                aria-label="Shift comment: ${escapeHtmlForTitle(shift.shiftComment)}"
                title="${escapeHtmlForTitle(shift.shiftComment)}"
                style="
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    margin-left: 4px;
                    cursor: pointer;
                    color: #000000;
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 1.2;
                    vertical-align: top;
                    flex-shrink: 0;
                "
            >ⓘ</span>`
            : '';
        
        // Always render with consistent spacing - use invisible placeholder when no color
        const dotStyle = dotColor
            ? `width: 10px;
               height: 10px;
               border-radius: 50%;
               background-color: ${dotColor};
               display: inline-block;
               flex-shrink: 0;
               border: 1px solid rgba(0,0,0,0.1);`
            : `width: 10px;
               height: 10px;
               display: inline-block;
               flex-shrink: 0;
               visibility: hidden;`;
        
        return `
            <div style="display: flex; align-items: center; gap: 6px; min-height: 20px;">
                <span style="${dotStyle}"></span>
                <span>${shift.displayText}</span>${tooltipIcon}${clockIcon}
            </div>
        `;
    });
    
    return `<div style="display: flex; flex-direction: column; gap: 4px;">${shiftHtmlLines.join('')}</div>`;
}

/**
 * Helper: Process cell rendering with metadata
 */
function processCellWithMetadata(
    metadata: ShiftMetadata,
    context: Record<string, unknown>
): string {
    const displayText = metadata.displayText || '';
    const shiftComment = metadata.shiftComment;
    const partialLeaveDuration = metadata.partialLeaveDuration;
    
    // Hidden leaves - don't show anything
    if (metadata.isHidden) {
        return '';
    }
    
    const mode = getColorMode(context);
    
    // Check if this cell has multiple shifts
    if (metadata.multipleShifts && metadata.multipleShifts.length > 0) {
        // NONE mode - render multiple shifts without colored dots
        if (mode === 'NONE') {
            const shiftsText = metadata.multipleShifts.map((s) => s.displayText).join('\n');
            // For multiple shifts in NONE mode, combine all comments and partial leaves
            const firstWithTooltip = metadata.multipleShifts.find((s) => s.partialLeaveDuration || s.shiftComment);
            const shiftComment = firstWithTooltip?.shiftComment;
            const partialLeave = firstWithTooltip?.partialLeaveDuration;
            return renderCellHtml(shiftsText, undefined, shiftComment, partialLeave);
        }
        
        // Render multiple shifts, each with its own colored dot
        return renderMultipleShifts(metadata.multipleShifts, mode, context);
    }
    
    // Single shift rendering (original behavior)
    // NONE mode - No colored dots
    if (mode === 'NONE') {
        return renderCellHtml(displayText, undefined, shiftComment, partialLeaveDuration);
    }
    
    // Get and normalize dot color
    const rawDotColor = getDotColor(metadata, mode, context);
    const dotColor = normalizeHexColor(rawDotColor);
    
    return renderCellHtml(displayText, dotColor, shiftComment, partialLeaveDuration);
}

/**
 * Create cell renderer for date columns
 */
export function createDateCellRenderer(dateCol: DateColumn) {
    return (params: { data?: Record<string, unknown>; context?: Record<string, unknown> }) => {
        const value = params.data?.[dateCol.field];
        const metadata = extractShiftMetadata(value);
        
        if (!metadata) {
            return value || '';
        }
        
        const context = params.context || {};
        return processCellWithMetadata(metadata, context);
    };
}
